import type { WhIssue, WhReportSummary, WhSeverityKey } from '@elva/ui';
import { growthGet, growthGetBlob, growthList, growthPost, growthPostAsync } from '@/data/http';
import { downloadBlob } from '@/data/download';
import type { BeSeoIssue } from '@/data/marketingApiTypes';
import { deepCamel, num, relativeTimeFromIso, str } from '@/data/adapt';
import { enumLower } from '@/data/adapt';

export const WEBSITE_HEALTH_PAGE_SIZE = 25;

export interface WebsiteHealthData {
  items: WhIssue[];
  summary: WhReportSummary;
}

const ISSUE_TYPE_LABELS: Record<string, string> = {
  missing_h1: 'Missing H1 heading',
  missing_title: 'Missing page title',
  missing_meta_description: 'Missing meta description',
  slow_page: 'Slow page load',
  broken_link: 'Broken link',
  missing_alt_text: 'Missing image alt text',
  duplicate_title: 'Duplicate page title',
  thin_content: 'Thin page content',
  noindex: 'Page blocked from search',
};

function humanizeIssueType(issueType: string): string {
  if (!issueType) return 'SEO issue';
  const labeled = ISSUE_TYPE_LABELS[issueType.toLowerCase()];
  if (labeled) return labeled;
  return issueType
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function mapSeverity(wire: string): WhSeverityKey {
  const status = enumLower(wire, 'minor' as string);
  if (status === 'passing' || status === 'good') return 'good';
  if (status === 'critical' || status === 'important' || status === 'minor') return status;
  return 'minor';
}

function extractSeoScore(raw: Record<string, unknown>): number {
  const seoScore = raw.seoScore ?? raw.seo_score;
  if (typeof seoScore === 'number') return seoScore;
  if (seoScore && typeof seoScore === 'object') {
    return num(deepCamel<Record<string, unknown>>(seoScore).score);
  }
  return num(raw.score);
}

function extractSeverityCounts(raw: Record<string, unknown>) {
  const bucket =
    raw.severityCounts ??
    raw.severity_counts ??
    raw.counts ??
    raw.issuesCount ??
    raw.issues_count ??
    {};
  const counts = deepCamel<Record<string, unknown>>(bucket as Record<string, unknown>);
  return {
    critical: num(counts.critical),
    important: num(counts.important),
    minor: num(counts.minor),
    good: num(counts.good ?? counts.passing),
  };
}

function formatScanNote(latestScan: Record<string, unknown> | undefined, scoreAsOf?: string): string {
  const scan = latestScan ? deepCamel<Record<string, unknown>>(latestScan) : undefined;
  const status = str(scan?.scanStatus, '').toLowerCase();
  const completedAt = str(scan?.scanCompletedAt ?? scoreAsOf, '');
  const pages = num(scan?.pagesAnalyzed);

  if (status !== 'completed' || !completedAt) {
    return 'No completed scan yet — scores populate after the first scanner run.';
  }

  const when = relativeTimeFromIso(completedAt);
  const pagesPart = pages > 0 ? ` · ${pages} pages analyzed` : '';
  return `Last scanned ${when}${pagesPart}`;
}

function buildSummary(
  auditRaw: Record<string, unknown> | undefined,
  summaryRaw: Record<string, unknown> | undefined,
  items: WhIssue[],
): WhReportSummary {
  const audit = auditRaw ? deepCamel<Record<string, unknown>>(auditRaw) : {};
  const summary = summaryRaw ? deepCamel<Record<string, unknown>>(summaryRaw) : {};

  const latestScan = (audit.latestScan ?? audit.latest_scan) as Record<string, unknown> | undefined;
  const seoScoreObj = (audit.seoScore ?? audit.seo_score) as Record<string, unknown> | undefined;
  const scoreAsOf = seoScoreObj
    ? str(deepCamel<Record<string, unknown>>(seoScoreObj).asOf)
    : str(audit.asOf);

  const score =
    extractSeoScore(summary) ||
    extractSeoScore(audit) ||
    0;

  const auditCounts = extractSeverityCounts(audit);
  const summaryCounts = extractSeverityCounts(summary);
  const counts = {
    critical: summaryCounts.critical || auditCounts.critical,
    important: summaryCounts.important || auditCounts.important,
    minor: summaryCounts.minor || auditCounts.minor,
    good: summaryCounts.good || auditCounts.good,
  };

  const patientsLostFromIssues = items.reduce((sum, issue) => sum + (issue.patientsLost ?? 0), 0);
  const patientsLost = num(audit.patientsLost ?? summary.patientsLost, patientsLostFromIssues);

  /** Backend puts money (cents) in `patients_lost_estimate`, not a patient count. */
  const revenueLostCents = num(
    audit.patientsLostEstimate ??
      audit.patients_lost_estimate ??
      summary.patientsLostEstimate ??
      summary.patients_lost_estimate ??
      audit.revenueLostCents ??
      audit.revenue_lost_cents ??
      summary.revenueLostCents ??
      summary.revenue_lost_estimate_cents,
  );

  const scanStatus = str(
    latestScan ? deepCamel<Record<string, unknown>>(latestScan).scanStatus : '',
    '',
  ).toLowerCase();

  return {
    score: Math.round(score * 10) / 10,
    counts,
    patientsLost,
    revenueLostCents,
    scanNote: formatScanNote(latestScan, scoreAsOf),
    hasCompletedScan: scanStatus === 'completed',
  };
}

function issueOneLiner(
  row: Record<string, unknown>,
  issueType: string,
  title: string,
): string {
  const explicit = str(row.oneLiner) || str(row.summary);
  if (explicit && explicit !== '—') return explicit;

  const affectedUrl = str(row.affectedUrl);
  const affectedCount = num(row.affectedCount);
  const fixStatus = str(row.fixStatus).toLowerCase();
  const dismissed = fixStatus === 'dismissed' ? ' · Dismissed' : '';

  if (affectedUrl) {
    return `${affectedUrl} — ${title.toLowerCase()}${dismissed}`;
  }
  if (affectedCount > 0) {
    const pageWord = affectedCount === 1 ? 'page' : 'pages';
    return `${affectedCount} ${pageWord} affected — ${title.toLowerCase()}${dismissed}`;
  }
  return `${title}${dismissed}`;
}

function adaptIssue(row: BeSeoIssue, index: number): WhIssue {
  const r = deepCamel<Record<string, unknown>>(row as Record<string, unknown>);

  const issueType = str(r.issueType);
  const title = str(r.title) || humanizeIssueType(issueType);
  const severityRaw = str(r.severityKey) || str(r.severity) || str(r.severityLabel);
  const severity = mapSeverity(severityRaw);

  const base: WhIssue = {
    id: typeof r.id === 'number' ? r.id : num(r.id, index + 1),
    severity,
    title,
    oneLiner: issueOneLiner(r, issueType, title),
  };

  const patientsLost = r.patientsLost ?? r.patientImpactEstimate;
  if (patientsLost != null) base.patientsLost = num(patientsLost);

  if (r.problem) base.problem = str(r.problem);
  if (r.effect) base.effect = str(r.effect);
  if (r.solution) base.solution = str(r.solution);

  const pages = Array.isArray(r.pages)
    ? (r.pages as unknown[]).map((page) => str(page)).filter(Boolean)
    : [];
  const affectedUrl = str(r.affectedUrl);
  if (pages.length) {
    base.pages = pages;
  } else if (affectedUrl) {
    base.pages = [affectedUrl];
  }

  return base;
}

export async function fetchWebsiteHealth(options?: {
  severity?: string;
  fixStatus?: string;
  page?: number;
  limit?: number;
}): Promise<WebsiteHealthData> {
  const page = options?.page ?? 1;
  const limit = options?.limit ?? 200;
  const params: Record<string, string | number> = { page, limit };
  if (options?.severity) params.severity = options.severity;
  if (options?.fixStatus) params.fix_status = options.fixStatus;

  const [audit, summary, issuesResult] = await Promise.all([
    growthGet<Record<string, unknown>>('/seo/audit/').catch(() => undefined),
    growthGet<Record<string, unknown>>('/seo/audit/summary/').catch(() => undefined),
    growthList<BeSeoIssue>('/seo/audit/issues/', params).catch(() => ({
      items: [] as BeSeoIssue[],
    })),
  ]);

  const items = issuesResult.items.map(adaptIssue);
  const reportSummary = buildSummary(audit, summary, items);

  return { items, summary: reportSummary };
}

export async function diagnoseSeoIssue(id: string | number) {
  return growthPostAsync(`/seo/audit/issues/${id}/diagnose/`);
}

export async function dismissSeoIssue(id: string | number) {
  return growthPost(`/seo/audit/issues/${id}/dismiss/`);
}

export async function exportSeoAuditPdf(): Promise<void> {
  const blob = await growthGetBlob('/seo/audit/export/', { format: 'pdf' });
  downloadBlob(blob, 'seo-audit.pdf');
}
